import { dssAnalyticsInit, dssTrack } from 'dss-analytics-manager';
import { getSlug, isV2Page, isOlp, isVisible } from 'global-scripts/utils/helpers';
import { deepGetOfType } from 'bam-deep-get';
import EventDefinitions from './glimpse/eventDefinitions';
import LifecycleHandlers from './glimpse/lifecycleHandlers';

const GlimpseV2Tracking = {};


/**
 * @description Manually track the pageView for the modal open
 * @param {HTMLElement} modalEl The modal opening HTML Element
 */
GlimpseV2Tracking.trackModalOpen = (modalEl) => {
    GlimpseV2Tracking.track({
        index: 0,
        target: modalEl,
        type: 'load-overlay'
    });

    const modalContainers = modalEl.querySelectorAll('[data-gv2-element="container"]');

    if (modalContainers.length) {
        modalContainers.forEach((modalContainer, i) => {
            GlimpseV2Tracking.track({
                index: i,
                target: modalContainer,
                type: 'inView'
            });
        });
    }
};

/**
 * @description Determine the page type (OLP, MLP, Splash)
 * @return welcome = OLP; welcome__mlp = MLP, welcome__splash = Splash
 */
GlimpseV2Tracking.getGlimpseV2PageType = function () {
    // Only V2 Pages have pageType on them, use isOlp for V1 logic
    const pageTypeMeta = document.querySelector('meta[name~="pageType"]');

    let pageType;
    if (pageTypeMeta) {
        pageType = pageTypeMeta.getAttribute('content');
    }

    if (pageType === 'olp') {
        return 'welcome';
    } else if (pageType === 'splash') {
        return 'welcome__splash';
    } else {
        return 'welcome__mlp';
    }
};

/**
 * @description Manually track the navigation. Nav state change needs to be track as the user scrolls up and down
 * the page.
 * @param {bool} isSticky Is the nav the sticky nav or not
 */
GlimpseV2Tracking.trackNav = (isSticky) => {
    const prefix = isSticky ? 'post' : 'pre';

    const target = document.querySelector(`nav.${prefix}-sticky`);

    GlimpseV2Tracking.track({
        index: 0,
        target,
        type: 'analytics_inView'
    });
};

/**
 * @description Pull the background asset url for the visible background asset for the hero cotnent
 * @returns {Object} The asset URI and type for the background image
 */
GlimpseV2Tracking.getBackgroundAssetSrc = () => {
    const assets = document.querySelectorAll('.hero .background-image__asset, .hero .background');
    const assetData = {
        src: null,
        type: null
    };

    let visibleEl;

    //Get first visible image if using css based responsive images.
    assets.forEach(asset => {
        if (!visibleEl && isVisible(asset)) {
            visibleEl = asset;
        }
    });

    if (visibleEl) {
        switch (visibleEl.tagName.toLowerCase()) {
            case 'div':
                if (visibleEl.style.backgroundImage) {
                    assetData.src = visibleEl.style.backgroundImage.slice(4, -1).replace(/["']/g, '');
                    assetData.type = 'image';
                }

                break;
            case 'img':
                assetData.type = 'image';
                assetData.src = visibleEl.src;
                break;
            case 'video':
                assetData.type = 'video';
                assetData.src = visibleEl.src;
                break;
        }

        if (assetData.src.indexOf('http') !== 0) {
            assetData.src = window.location.protocol + assetData.src;
        }
    }

    return assetData;
};

/**
 * @description Determine the element name for CTA links and buttons.
 *  Most CTAs will specify their own name but sign-up
 * button name are determined by their deeplink.
 * @param {HTMLElement} el The CTA html element
 * @returns {String} The element name
 */
GlimpseV2Tracking.getElementName = (el) => {
    let name = el.dataset.gv2Name;

    if (el.href && !name) {
        const url = new URL(el.href);

        if (url.pathname.includes('sign-up')) {
            if (url.searchParams.has('type')) {
                name = (url.searchParams.get('type') !== 'bundle') ? 'standalone_cta' : 'bundle_cta';
            } else if (url.searchParams.has('efid')) {
                name = 'sign_up_to_access';
            }
        } else if (/^modal/.test(el.getAttribute('href'))) {
            name = 'mlp_modal_link';
        }
    }

    return name;
};

/**
 * @description Build GlimpseV2 payload and fire glimpseV2 tracking based upon DOM attributes.
 * @param {Event} event The fired tracking event
 * @param {Function} callback A function to run after the analytics fires
 */
GlimpseV2Tracking.track = (event, callback = () => {}) => {
    const { target, type, index = 0 } = event;

    let data = GlimpseV2Tracking.initData();

    const slug = getSlug();
    const elements = [];
    let parentContainer;
    let backgroundAsset;
    let abortTrack = false;
    let elementName;
    let elementId;
    let pageKey;
    let payloadData = {};

    switch (type.replace('analytics_', '')) {
        case 'load':
            data = GlimpseV2Tracking.initPageViewPayload(data);
            backgroundAsset = GlimpseV2Tracking.getBackgroundAssetSrc();

            payloadData = {
                backgroundAsset: backgroundAsset.src,
                backgroundAssetType: backgroundAsset.type,
                backgroundImage: backgroundAsset.src,
                pageId: slug,
                pageKey: slug,
                pageName: GlimpseV2Tracking.getGlimpseV2PageType()
            };

            break;
        case 'load-overlay':
            data = GlimpseV2Tracking.initPageViewPayload(data);
            data.common.isOverlay = true;

            pageKey = deepGetOfType(target, 'dataset.gv2Key', String);

            payloadData = {
                pageId: pageKey,
                pageKey,
                pageName: 'mlp_modal'
            };

            break;
        case 'click':
            data = GlimpseV2Tracking.initInteractionPayload(data);

            parentContainer = target.closest('[data-gv2-element=\'container\']');
            if (!parentContainer) {
                abortTrack = true;
            }

            elementName = GlimpseV2Tracking.getElementName(target);
            elementId = target.getAttribute('data-analytics-name') ? target.getAttribute('data-analytics-name') : elementName;
            if (/mlp_modal_link|modal_cta/.test(elementName)) {
                elementId = target.getAttribute('href');
            }

            if (!parentContainer || !elementName) {
                abortTrack = true;
            }

            payloadData = {
                containerKey: deepGetOfType(parentContainer, 'dataset.gv2Key', String),
                elementId,
                elementIdType: target.dataset.gv2Key,
                elementName,
                elementType: target.dataset.gv2Type || target.dataset.gv2Key
            };

            break;
        case 'inView':
            if (target) {
                data = GlimpseV2Tracking.initContainerViewPayload(data);
                target.querySelectorAll('[data-gv2-element="element"]').forEach((button, i)=> {
                    elementName = GlimpseV2Tracking.getElementName(button);
                    elementId = button.getAttribute('data-analytics-name') ? button.getAttribute('data-analytics-name') : elementName;

                    if (elementName) {
                        if (/mlp_modal_link|modal_cta/.test(elementName)) {
                            elementId = button.getAttribute('href');
                        }
                        elements.push({
                            elementId,
                            elementIdType: button.dataset.gv2Key,
                            elementIndex: i,
                            mediaFormatType: 'not_applicable'
                        });
                    }
                });

                if (!elements.length) {
                    elements.push({
                        elementId: 'mlp_body',
                        elementIdType: 'text_detail',
                        elementIndex: 0
                    });
                }

                payloadData = {
                    containerKey: target.dataset.gv2Key,
                    containerType: target.dataset.gv2Type,
                    contentTransactionId: null,
                    elements: elements.length ? elements : null,
                    elementsPerWidth: elements.length,
                    horizontalPosition: 0,
                    verticalPosition: index
                };
            }

            break;
    }

    if (!abortTrack) {
        data.payload = {
            ...data.payload,
            ...payloadData
        };

        GlimpseV2Tracking.makeGlimpseCall(data, callback);
    } else {
        callback();
    }
};

/**
 * @description Handler for the IntersectionObserver when element
 * comes into view. Once an element has come into view
 * they are unregistered with the IntersectionObserver.
 * @param {*} entries All items that are registered with observer
 * @param {*} observer The IntersectionObserver object
 */
GlimpseV2Tracking.onIntersect = (entries) => {
    entries.forEach((entry, index) => {
        const { target } = entry;

        if (target && entry.isIntersecting) {
            const verticalPosition = parseInt(target.dataset.gv2Index, 10) || index;

            GlimpseV2Tracking.track({
                index: verticalPosition,
                target,
                type: 'analytics_inView'
            });
        }
    });
};

/**
 * @description Extra functionality that needs to be run post interaction tracking
 * @param {HTMLElement} targetEl The HTMLElement that was clicked on.
 */
GlimpseV2Tracking.postClickHandler = (targetEl) => {
    const href = targetEl.getAttribute('href');

    if (/^#*modal/.test(href)) {
        const modal = document.getElementById(href.replace('#', ''));

        setTimeout(() => {
            if (modal && modal.classList.contains('open')) {
                GlimpseV2Tracking.trackModalOpen(modal);
            }
        }, 300);
    }
};

/**
 * @description Handler for analytics_click
 * @param {Event} e The analytics_click event
 */
GlimpseV2Tracking.onClickHandler = (e) => {
    //Only fire track for elements that have the gv2-asset class.
    const target = e.currentTarget;

    if (target.hasAttribute('data-gv2-element') && target.getAttribute('data-gv2-element') === 'element') {
        GlimpseV2Tracking.track({
            target,
            type: e.type
        }, () => {
            GlimpseV2Tracking.postClickHandler(target);
        });
    }
};

/**
 * @description Initialize GlimpseV2 by adding listeners for the analytics_load and analytics_click events.
 * IntersectionObserver is registered if there are any gv2-containers that need to be observed.
 */
GlimpseV2Tracking.initGlimpseV2 = () => {
    const featureConfig = window.dssMlpConfigs.featureConfig || window.dssMlpConfigs.killswitch;

    if (!featureConfig.disableGlimpseV2) {
        const config = {
            enableValidation: featureConfig.enableAnalyticsValidation,
            glimpse_v2: {
                GlimpseEvent: window.AnalyticsManager.glimpseEvent,
                GlimpseEvents: window.AnalyticsManager.glimpseEvents,
                UserActivityApi: window.AnalyticsManager.userActivityApi
            }
        };

        GlimpseV2Tracking.trackDefinedEvent('appEvents', 'lifecycle', 'appStart');
        GlimpseV2Tracking.trackDefinedEvent('appEvents', 'lifecycle', 'appLaunched');
        GlimpseV2Tracking.trackDefinedEvent('appEvents', 'lifecycle', 'appDeepLink');

        const containers = document.querySelectorAll('[data-gv2-element="container"]:not(nav):not(.modal-content)');

        if (containers.length) {
            const intersect = new IntersectionObserver(GlimpseV2Tracking.onIntersect, {
                threshold: 0.1
            });

            containers.forEach(el => intersect.observe(el));
        }
        dssAnalyticsInit(config);

        document.querySelectorAll('[data-gv2-element="element"]').forEach(el => {
            el.addEventListener('click', (e) => {
                GlimpseV2Tracking.onClickHandler(e);
            }, true);
        });

        document.addEventListener('analytics_load', GlimpseV2Tracking.track);
    }
};

/**
 * Makes the call to dssTrack with the provided data.
 * @param {Object} data GlimpseV2 data payload to be sent
 * @param {Function} callback A function to run after the analytics fires
 */
GlimpseV2Tracking.makeGlimpseCall = (data, callback) => {
    const featureConfig = window.dssMlpConfigs.featureConfig || window.dssMlpConfigs.killswitch;

    dssTrack({
        glimpse_v2: data
    }).then(() => {
        callback();
    }).catch(error => {
        console.log(featureConfig);

        if (featureConfig.enableAnalyticsValidation) {
            console.error(error, data);
        }
        callback();
    });
};

/**
 * Generated and returns the common data needed for tracking
 * @returns {Object} Data object with some common items preset.
 */
GlimpseV2Tracking.initData = () => {
    const { AnalyticsManager } = window;
    const { contextData } = AnalyticsManager;
    const featureConfig = window.dssMlpConfigs.featureConfig || window.dssMlpConfigs.killswitch;

    const data = {
        common: {
            appName: deepGetOfType(window, 'server_path.analytics.glimpse.appName', String),
            appVersion: contextData.appVersion,
            consentStates: contextData.consentStatus.split(','),
            experiments: [],
            isAuthenticated: contextData.isLoggedIn || false,
            isOverlay: false,
            partner: contextData.partner,
            platform: 'web'
        },
        config: {
            enableValidation: !!(featureConfig.enableAnalyticsValidation)
        },
        event: '',
        payload: {}
    };

    if (contextData.optimizelyCampaignId) {
        data.common.experiments.push({
            experimentFeatureId: contextData.optimizelyCampaignId,
            experimentId: contextData.optimizelyExperimentId,
            experimentPlatform: 'optimizely',
            variantId: contextData.optimizelyVariationId
        });
    }

    return data;
};

/**
 * @description Manipulates the data payload for the pageView event
 * @param {Object} data The current state of ther data payload
 * @returns {Object} Manipulated data object
 */
GlimpseV2Tracking.initPageViewPayload = (data) => {
    const { AnalyticsManager } = window;
    const { contextData } = AnalyticsManager;

    data.event = 'pageView';
    data.payload = {
        appLanguage: contextData.appLanguage || contextData.deviceLanguage,
        backgroundAsset: null,
        backgroundAssetType: null,
        backgroundImage: null,
        collectionGroupKey: null,
        collectionId: null,
        destination: document.location.href,
        deviceLanguage: contextData.deviceLanguage,
        pageId: '',
        pageKey: '',
        pageName: '',
        referrer: document.referrer
    };

    return data;
};

/**
 * @description Manipulates the data payload for the containerView event
 * @param {Object} data The current state of ther data payload
 * @returns {Object} Manipulated data object
 */
GlimpseV2Tracking.initContainerViewPayload = (data) => {
    data.event = 'containerView';
    data.payload = {
        containerKey: '',
        containerType: '',
        contentTransactionId: null,
        elements: null,
        elementsPerWidth: 0,
        horizontalPosition: 0,
        verticalPosition: 0
    };

    return data;
};

/**
 * @description Manipulates the data payload for the interaction event
 * @param {Object} data The current state of ther data payload
 * @returns {Object} Manipulated data object
 */
GlimpseV2Tracking.initInteractionPayload = (data) => {
    data.event = 'interaction';
    data.payload = {
        containerKey: '',
        contentKeys: {
            collectionId: null,
            contentId: null,
            encodedFamilyId: null,
            encodedSeriesId: null,
            mediaId: null,
            programId: null
        },
        contentType: 'other',
        elementId: '',
        elementIdType: '',
        elementName: '',
        elementType: '',
        elementsInView: 1,
        interactionType: 'select',
        mediaFormatType: 'not_applicable',
        mediaType: 'other',
        programType: 'other'
    };

    return data;
};

GlimpseV2Tracking.initLifeCyclePayload = (data) => {
    data.event = '';
    data.payload = {
        adobeId: window.AnalyticsManager.deviceData.mid,
        consentFallback: (!window.Optanon),
        correlationId: JSON.parse(sessionStorage.getItem('correlationId')),
        kochavaAppId: 'id_not_available'
    };

    return data;
};

/**
 * @description Makes GlimpseV2 tracking calls based off of pre-defined Event definitions and not the DOM.
 * Useful for tracking items not controlled by the CMS.
 * @param {String} defintionKey The key for the set of eventDefintion to be used
 * @param {String} eventType The type of event to be tracked pageView, containerView, interaction or input only
 * @param {String} eventKey The key used to retrieve the specific set of tracking data for that eventType
 * @param {Function} callback A function to run after the analytics fires
 */
GlimpseV2Tracking.trackDefinedEvent = (defintionKey, eventType, eventKey = '', callback = () => {}) => {
    let data = GlimpseV2Tracking.initData();
    let abortTrack = false;
    let payloadData;

    const glimpseDefintionSet = EventDefinitions[defintionKey];

    if (glimpseDefintionSet) {
        switch (eventType) {
            case 'pageView':
                data = GlimpseV2Tracking.initPageViewPayload(data);
                payloadData = (eventKey && glimpseDefintionSet.pageView[eventKey]) ?
                    glimpseDefintionSet.pageView[eventKey] : glimpseDefintionSet.pageView;

                break;
            case 'containerView':
                data = GlimpseV2Tracking.initContainerViewPayload(data);
                payloadData = (eventKey && glimpseDefintionSet.containerView[eventKey]) ?
                    glimpseDefintionSet.containerView[eventKey] : null;

                break;
            case 'interaction':
                data = GlimpseV2Tracking.initInteractionPayload(data);
                payloadData = (eventKey && glimpseDefintionSet.interaction[eventKey]) ?
                    glimpseDefintionSet.interaction[eventKey] : null;
                break;
            case 'lifecycle':
                data = GlimpseV2Tracking.initLifeCyclePayload(data);
                payloadData = (eventKey && glimpseDefintionSet.lifecycle[eventKey]) ?
                    glimpseDefintionSet.lifecycle[eventKey] : null;

                if (payloadData) {
                    data.event = payloadData.eventUrn;
                    data.config.hierarchyBypass = !!payloadData.hierarchyBypass;

                    if (LifecycleHandlers[eventKey]) {
                        payloadData = {
                            ...LifecycleHandlers[eventKey](),
                            ...payloadData.payload
                        };
                    } else {
                        payloadData = payloadData.payload;
                    }

                    if (eventKey === 'appLaunched') {
                        payloadData.inboundUrl = document.referrer;
                    }
                }
                break;
            default:
                abortTrack = true;
                break;
        }
    } else {
        abortTrack = true;
    }

    if (!abortTrack) {
        if (payloadData) {
            data.payload = {
                ...data.payload,
                ...payloadData
            };
        } else {
            abortTrack = true;
        }

        GlimpseV2Tracking.makeGlimpseCall(data, callback);
    } else {
        callback();
    }
};

export default GlimpseV2Tracking;
